import React from 'react';
import { Select } from 'antd';
import { Card } from '../../../components/CssFrameworkComponents';
import SaveIndicator from '../components/SaveIndicator';
import { useAutoSaveSetting } from '../hooks/useAutoSaveSetting';
import { MILLISECONDS_PER_SECOND } from '../api/settingsApiSlice';
import { useIsDisabledForRole } from '../../../components/ButtonWithRole';
import { usePrerenderUser } from '../../../hooks/usePrerenderUser';
import { withSelectedOption } from '../selectOption';
import { SETTINGS_RESTRICTED_ROLES } from '../settingsRoles';

const TITLE = 'Rendering timeout';
const SECONDS_LABEL = 'seconds';
const CUSTOM_LABEL = 'Custom';

const DESCRIPTIONS = [
  'With this option, you can configure how long the rendering process should wait before raising a timeout error.',
  // eslint-disable-next-line max-len
  "Setting this to a long interval could result in connection timeouts or trigger the crawler's timeout. Setting this option to a short interval could result in a premature cut-off of the rendering, as the rendering process will quit when it's finished. You can reduce the maximum timeout if your website does not have a long loading time and receives a notable amount of invalid requests.",
  // eslint-disable-next-line max-len
  "After changing this option from the initial most stable setting, please monitor your rendering errors for timeouts as this can heavily influence the product's quality.",
];

const PRESET_TIMEOUTS = [5, 10, 20, 25, 30, 60];

const RenderTimeoutCard = () => {
  const { settings } = usePrerenderUser();
  const { save, status, retry } = useAutoSaveSetting();
  const disabledForRole = useIsDisabledForRole(SETTINGS_RESTRICTED_ROLES);

  const currentTimeout = settings.pageLoadTimeout / MILLISECONDS_PER_SECOND;

  const options = PRESET_TIMEOUTS.map((id) => ({ text: `${id} ${SECONDS_LABEL}`, id }));

  const allOptions = withSelectedOption(
    options,
    (option) => option.id === currentTimeout,
    // The synthesized custom entry is display-only: `parseInt('custom')` is NaN, which the API
    // rejects with a 400, so it must never be selectable.
    () => ({ text: `${CUSTOM_LABEL} - (${currentTimeout} ${SECONDS_LABEL})`, id: 'custom', disabled: true })
  );
  const currentTimeoutId = allOptions.find((option) => option.selected).id;

  const onTimeoutChanged = (value) => {
    save({ pageLoadTimeout: parseInt(value, 10) });
  };

  return (
    <Card title={TITLE}>
      <div className="col">
        {DESCRIPTIONS.map((description) => (
          <div className="row" key={description}>
            <p>{description}</p>
          </div>
        ))}
        <div className="row">
          <div className="col">
            <Select
              name="timeout"
              id="timeout"
              onChange={onTimeoutChanged}
              // Controlled by the stored setting, not the last click: a failed save leaves the
              // server state untouched, so the select snaps back to what is actually persisted
              // instead of advertising a value that never landed. A successful save moves it
              // forward via the getPrerenderUser refresh the mutation dispatches.
              value={currentTimeoutId}
              disabled={disabledForRole || status === 'saving'}
              style={{ width: '100%' }}
            >
              {allOptions.map((option, i) => (
                <Select.Option value={option.id} key={i} disabled={option.disabled}>
                  {option.text}
                </Select.Option>
              ))}
            </Select>
          </div>
          <SaveIndicator status={status} onRetry={retry} />
        </div>
      </div>
    </Card>
  );
};

export default RenderTimeoutCard;
